import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export interface SearchParamWriteOptions {
  /** `replace` (the default) rewrites the current history entry; `push` adds one, so Back undoes it. */
  history?: 'replace' | 'push'
  /** History state for the new entry. Left out, `replace` keeps the current entry's state and `push` has none. */
  state?: unknown
}

export interface SearchParamStateOptions {
  /**
   * False keeps the value in component state and leaves the URL alone — for a
   * widget that is embedded in another page and shouldn't write into its URL.
   */
  enabled?: boolean
}

/**
 * Writes made in the same event handler all start from the same render's URL,
 * so a second write would drop the first. This remembers the last one until
 * the handler is done, and the next write builds on it.
 */
let pending: { from: string; search: string } | null = null

/**
 * One search parameter as a piece of state: `?status=planned` reads as
 * "planned", and an absent parameter reads as `defaultValue`. Writing the
 * default removes the parameter, so the URL only holds what was changed.
 */
export function useSearchParamState(
  key: string,
  defaultValue: string,
  { enabled = true }: SearchParamStateOptions = {},
): [string, (next: string, options?: SearchParamWriteOptions) => void] {
  const location = useLocation()
  const navigate = useNavigate()
  const [localValue, setLocalValue] = useState(defaultValue)

  const urlValue = new URLSearchParams(location.search).get(key)
  const value = enabled ? (urlValue ?? defaultValue) : localValue

  const setValue = useCallback(
    (next: string, { history = 'replace', state }: SearchParamWriteOptions = {}) => {
      if (!enabled) {
        setLocalValue(next)
        return
      }
      const base = pending && pending.from === location.search ? pending.search : location.search
      const params = new URLSearchParams(base)
      if (next === defaultValue) params.delete(key)
      else params.set(key, next)
      const search = params.toString()
      const nextSearch = search ? `?${search}` : ''
      if (!pending) queueMicrotask(() => { pending = null })
      pending = { from: location.search, search: nextSearch }
      if (nextSearch === base) return
      navigate(
        { pathname: location.pathname, search: nextSearch, hash: location.hash },
        {
          replace: history === 'replace',
          state: state !== undefined ? state : history === 'replace' ? location.state : undefined,
        },
      )
    },
    [enabled, key, defaultValue, location.pathname, location.search, location.hash, location.state, navigate],
  )

  return [value, setValue]
}
